'use client';

// FeaturesDropdown — panneau qui s'affiche au survol d'un item de la navbar
// configure avec un sous-menu. Supporte 2 modes :
//
//   mode="features" : grille issue de settings.features. Si filterIds non
//                     vide, on n'affiche que les features dont l'ID (ou clé
//                     fallback "t:<title>") est dedans. Sinon toutes les
//                     features actives sont affichees.
//
//   mode="custom"   : grille libre d'items {label, url, icon, target}
//                     definis dans l'admin (sous-menu personnalise).
//
// Style : popup blanc avec ombre, grille 3 colonnes responsive, animation
// fade-in courte. Utilise lucide-react via le nom d'icone string.

import * as Icons from 'lucide-react';

// Cle stable pour identifier une feature : id (UUID) si dispo, sinon slug
// du titre. DOIT matcher la logique cote admin VitrineHeader (FeaturesPicker).
function featureKey(f) {
  if (!f) return null;
  if (f.id) return f.id;
  if (f.title) return 't:' + String(f.title).trim().toLowerCase();
  return null;
}

export default function FeaturesDropdown({
  mode = 'features',
  features = [],
  filterIds = [],
  customItems = [],
  onItemClick,
}) {
  // Construit la liste d'items unifiee a afficher dans le dropdown
  // selon le mode demande.
  let items = [];
  if (mode === 'custom') {
    items = (customItems || [])
      .filter(it => it && (it.label || it.url || it.icon))
      .map(it => ({
        label: it.label || '',
        url: it.url || '',
        target: it.target || '_self',
        icon: it.icon || 'Sparkles',
      }));
  } else {
    // mode "features"
    const visible = (features || []).filter(f => f && (f.active !== false));
    const filtered = (filterIds && filterIds.length > 0)
      ? visible.filter(f => filterIds.includes(featureKey(f)))
      : visible;
    items = filtered.map(f => ({
      label: f.title || 'Fonctionnalité',
      url: f.url || '/fonctionnalites',
      target: f.target || '_self',
      icon: f.icon || 'Sparkles',
    }));
  }

  if (items.length === 0) return null;

  return (
    <div
      role="menu"
      style={{
        position: 'absolute',
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginTop: '8px',
        background: '#ffffff',
        borderRadius: '16px',
        boxShadow: '0 24px 48px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)',
        border: '1px solid var(--color-border, #e2e8f0)',
        padding: '20px',
        minWidth: '720px',
        maxWidth: 'calc(100vw - 48px)',
        zIndex: 100,
        animation: 'ligoboxDropdownFade 0.18s ease-out',
      }}
    >
      <style>{`
        @keyframes ligoboxDropdownFade {
          from { opacity: 0; transform: translate(-50%, -8px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>

      <div style={{
        marginBottom: '14px',
        paddingBottom: '12px',
        borderBottom: '1px solid var(--color-border, #f1f5f9)',
        display: 'flex',
        alignItems: 'baseline',
        gap: '12px',
      }}>
        <span style={{
          fontSize: '13px',
          fontWeight: 700,
          color: 'var(--color-text, #0f172a)',
          letterSpacing: '0.02em',
        }}>
          Découvrez nos fonctionnalités :
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '8px',
      }}>
        {items.map((it, i) => (
          <DropdownItem key={i} item={it} onClick={onItemClick} />
        ))}
      </div>

      {/* Bouton "voir toutes les fonctionnalites" — seulement en mode features */}
      {mode === 'features' && (
        <div style={{
          marginTop: '14px',
          paddingTop: '12px',
          borderTop: '1px solid var(--color-border, #f1f5f9)',
          textAlign: 'right',
        }}>
          <a
            href="/fonctionnalites"
            onClick={onItemClick}
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--color-primary, #e27228)',
              textDecoration: 'none',
            }}
          >
            Voir toutes les fonctionnalités →
          </a>
        </div>
      )}
    </div>
  );
}

function DropdownItem({ item, onClick }) {
  const IconComp = Icons[item.icon] || Icons.Sparkles;
  const href = item.url || '#';
  const target = item.target || '_self';

  return (
    <a
      href={href}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 12px',
        borderRadius: '10px',
        textDecoration: 'none',
        color: 'var(--color-text, #0f172a)',
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-alt, #f8fafc)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <span style={{
        flex: '0 0 auto',
        width: '36px',
        height: '36px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        background: 'rgba(226, 114, 40, 0.08)',
        color: 'var(--color-primary, #e27228)',
      }}>
        <IconComp size={18} strokeWidth={2} />
      </span>
      <span style={{
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: 1.3,
      }}>
        {item.label || 'Lien'}
      </span>
    </a>
  );
}
